import { CheckCircle, Clock, XCircle, Circle, User } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Process, ProcessApproval } from '../../types/response'

interface ApprovalStep {
    label: string
    status: 'completed' | 'pending' | 'rejected' | 'not_started'
    approver?: string
    approvedAt?: string
    notes?: string
}

interface ProcessApprovalProgressProps {
    process: Process
}

const ProcessApprovalProgress: React.FC<ProcessApprovalProgressProps> = ({
    process,
}) => {
    // Helper to format date
    const formatDateTime = (dateStr?: string) => {
        if (!dateStr) return '-'
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const techLeadApproval = process.approvals?.find(
        (approval) => approval.approver_role === 'tech_lead'
    )
    // Support both 'storekeeper' (new) and 'warehouse_keeper' (legacy)
    const warehouseKeeperApproval = process.approvals?.find(
        (approval) =>
            approval.approver_role === 'storekeeper' ||
            approval.approver_role === 'warehouse_keeper'
    )

    const getStatus = (
        approval?: ProcessApproval
    ): 'completed' | 'pending' | 'rejected' | 'not_started' => {
        if (!approval) return 'not_started'
        const action = approval.action?.toUpperCase() || ''
        // CONFIRM-PN, CONFIRM-PX, CONFIRM-PDC, CONFIRM-PKK, approved, approve
        if (
            action.startsWith('CONFIRM') ||
            action === 'APPROVED' ||
            action === 'APPROVE'
        )
            return 'completed'
        // REJECT-PN, REJECT-PX, REJECT-PDC, REJECT-PKK, rejected, reject
        if (
            action.startsWith('REJECT') ||
            action === 'REJECTED' ||
            action === 'REJECT'
        )
            return 'rejected'
        return 'pending'
    }

    // Build approval steps
    const steps: ApprovalStep[] = [
        {
            label: 'Created',
            status: 'completed',
            approvedAt: formatDateTime(process?.created_at),
        },
        {
            label: 'Tech Lead Approval',
            status: getStatus(techLeadApproval),
            approvedAt: formatDateTime(techLeadApproval?.created_at),
            notes: techLeadApproval?.comments,
        },
        {
            label: 'Warehouse Keeper Approval',
            status: getStatus(warehouseKeeperApproval),
            approvedAt: formatDateTime(warehouseKeeperApproval?.created_at),
            notes: warehouseKeeperApproval?.comments,
        },
        {
            label: 'Completed',
            status:
                process?.status === 'completed' ? 'completed' : 'not_started',
        },
    ]

    const getStepIcon = (status: ApprovalStep['status']) => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="w-5 h-5 text-green-600" />
            case 'rejected':
                return <XCircle className="w-5 h-5 text-red-600" />
            case 'pending':
                return <Clock className="w-5 h-5 text-yellow-600" />
            default:
                return <Circle className="w-5 h-5 text-gray-300" />
        }
    }

    const getStatusBadge = (status: ApprovalStep['status']) => {
        switch (status) {
            case 'completed':
                return <Badge variant="default">Completed</Badge>
            case 'rejected':
                return <Badge variant="destructive">Rejected</Badge>
            case 'pending':
                return <Badge variant="secondary">Pending</Badge>
            default:
                return <Badge variant="outline">Not Started</Badge>
        }
    }

    return (
        <>
            <Card className="mt-2" data-tour="approval-progress-card">
                <CardHeader>
                    <CardTitle className="text-sm">Approval Progress</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12"></TableHead>
                                    <TableHead>Step</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Date & Time</TableHead>
                                    <TableHead>Notes</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {steps.map((step, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {getStepIcon(step.status)}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {step.label}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(step.status)}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {step.approvedAt}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {step.notes || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
            <ProcessApprovalHistory process={process} />
        </>
    )
}

const ProcessApprovalHistory: React.FC<ProcessApprovalProgressProps> = ({
    process,
}) => {
    if (!process.approvals || process.approvals.length === 0) {
        return null
    }

    return (
        <Card className="mt-2" data-tour="approval-history-card">
            <CardHeader>
                <CardTitle className="text-sm">Approval History</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Role</TableHead>
                                <TableHead>Action</TableHead>
                                <TableHead>Date & Time</TableHead>
                                <TableHead>Approver</TableHead>
                                <TableHead>Comments</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {process.approvals.map((approval) => (
                                <TableRow key={approval.id}>
                                    <TableCell className="font-medium capitalize">
                                        {approval.approver_role.replace(
                                            '_',
                                            ' '
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={(() => {
                                                const action =
                                                    approval.action?.toUpperCase() ||
                                                    ''
                                                if (
                                                    action.startsWith(
                                                        'CONFIRM'
                                                    ) ||
                                                    action === 'APPROVED' ||
                                                    action === 'APPROVE'
                                                )
                                                    return 'default'
                                                if (
                                                    action.startsWith(
                                                        'REJECT'
                                                    ) ||
                                                    action === 'REJECTED'
                                                )
                                                    return 'destructive'
                                                return 'secondary'
                                            })()}
                                        >
                                            {approval.action}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {new Date(
                                            approval.created_at
                                        ).toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4" />
                                            {approval.approver_name}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {approval.comments || '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    )
}

export default ProcessApprovalProgress
